import React from 'react';
import { Youtube, Video as VideoIcon } from 'lucide-react';

interface PlatformIconProps {
  platform?: string | null;
  className?: string;
  size?: number;
}

/**
 * PlatformIcon component - displays the appropriate icon for each video platform
 * Supports YouTube, Twitch, TikTok, and a generic video icon for uploaded files
 */
const PlatformIcon: React.FC<PlatformIconProps> = ({ platform, className = '', size = 16 }) => {
  // If no platform or it's null, show a generic upload icon
  if (!platform) {
    return (
      <div className={`inline-flex items-center justify-center ${className}`} title="Uploaded video">
        <VideoIcon size={size} />
      </div>
    );
  }

  const platformLower = platform.toLowerCase();

  // YouTube Icon
  if (platformLower === 'youtube') {
    return (
      <div className={`inline-flex items-center justify-center ${className}`} title="YouTube">
        <Youtube size={size} className="text-red-600" />
      </div>
    );
  }

  // Twitch Icon (custom SVG since lucide-react doesn't have it)
  if (platformLower === 'twitch') {
    return (
      <div className={`inline-flex items-center justify-center ${className}`} title="Twitch">
        <svg 
          width={size} 
          height={size} 
          viewBox="0 0 24 24" 
          fill="currentColor"
          className="text-purple-600"
        >
          <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
        </svg>
      </div>
    );
  }

  // TikTok Icon (custom SVG)
  if (platformLower === 'tiktok') {
    return (
      <div className={`inline-flex items-center justify-center ${className}`} title="TikTok">
        <svg 
          width={size} 
          height={size} 
          viewBox="0 0 24 24" 
          fill="currentColor"
          className="text-black"
        >
          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
        </svg>
      </div>
    );
  }

  // Instagram/Facebook (generic video icon for now)
  if (platformLower === 'instagram' || platformLower === 'facebook') {
    return (
      <div className={`inline-flex items-center justify-center ${className}`} title={platform}>
        <VideoIcon size={size} className="text-pink-600" />
      </div>
    );
  }

  // Default fallback for unknown platforms
  return (
    <div className={`inline-flex items-center justify-center ${className}`} title={platform || 'Video'}>
      <VideoIcon size={size} />
    </div>
  );
};

export default PlatformIcon;

